import { useEffect, useState } from "react";
import { X, Sparkles, Check, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { errMessage } from "../lib/api";

const LIMIT_LABELS = {
  saved_markets: "Saved markets",
  alerts_markets: "Alert-enabled markets",
  ai_insights_per_month: "AI insights this month",
};

const PRO_FEATURES = [
  "Unlimited saved markets",
  "Unlimited alerts",
  "Unlimited AI insights",
  "SMS + email alerts",
];

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setInfo(e.detail || {});
      setOpen(true);
    };
    window.addEventListener("ws:upgrade-required", handler);
    return () => window.removeEventListener("ws:upgrade-required", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handler/setState are stable
  }, []);

  if (!open) return null;

  const upgrade = async () => {
    setBusy(true);
    const planKey = cycle === "yearly" ? "pro_yearly" : "pro_monthly";
    try {
      const r = await api.post("/billing/checkout", {
        plan_key: planKey,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(errMessage(e, "Could not start upgrade"));
      setBusy(false);
    }
  };

  const limitLabel = LIMIT_LABELS[info?.limit] || "Plan limit";
  const limitValue = info?.limit_value;

  return (
    <div
      data-testid="upgrade-modal"
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 animate-fade-up"
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-white max-w-md w-full border border-[--ink]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[--line] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} strokeWidth={1.5} className="text-[--accent]" />
            <div className="overline">Upgrade to Pro</div>
          </div>
          <button
            data-testid="upgrade-modal-close"
            onClick={() => setOpen(false)}
            className="p-1 hover:bg-[--bg-soft]"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="bg-[--bg-soft] border border-[--line] p-3 mb-5 text-xs font-mono">
            <span className="text-[--ink-muted]">Hit your Starter limit:</span>{" "}
            <strong>{limitLabel}</strong>
            {limitValue !== undefined && limitValue !== null && (
              <span className="text-[--ink-muted]"> · cap {limitValue}</span>
            )}
          </div>

          <h2 className="font-display text-2xl font-bold tracking-tighter mb-2">
            Go Pro. Stop hitting walls.
          </h2>
          <p className="text-sm text-[--ink-soft] mb-5">
            Unlock unlimited saved markets, alerts, and AI insights. Plus SMS alerts straight to your phone.
          </p>

          <div className="flex gap-1 mb-5 border border-[--line]">
            {["monthly", "yearly"].map((c) => (
              <button
                key={c}
                data-testid={`upgrade-cycle-${c}`}
                onClick={() => setCycle(c)}
                className={`flex-1 py-2 text-xs uppercase tracking-wider transition-colors ${
                  cycle === c ? "bg-[--ink] text-white" : "text-[--ink-muted] hover:text-[--ink]"
                }`}
              >
                {c === "monthly" ? "$99/mo" : "$83/mo · billed yearly"}
              </button>
            ))}
          </div>

          <ul className="space-y-2 mb-6 text-sm">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check size={14} strokeWidth={2} className="text-[--accent] mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="upgrade-modal-cta"
            onClick={upgrade}
            disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3"
          >
            <Zap size={14} strokeWidth={1.5} />
            {busy ? "Redirecting…" : "Upgrade to Pro →"}
          </button>

          <p className="text-[10px] text-[--ink-muted] text-center mt-3 font-mono uppercase tracking-wider">
            Card on file · no re-entry needed · cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}
